import React from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import classNames from 'classnames';

import { getConfig } from '@edx/frontend-platform';
import { useToggle } from '@openedx/paragon';

import { CourseTabsNavigation } from '../course-tabs';
import { useModel } from '../generic/model-store';
import { AlertList } from '../generic/user-messages';
import StreakModal from '../shared/streak-celebration';
import InstructorToolbar from '../instructor-toolbar';
import useEnrollmentAlert from '../alerts/enrollment-alert';
import useLogistrationAlert from '../alerts/logistration-alert';

import { injectIntl, intlShape } from '@edx/frontend-platform/i18n';
import messages from '../course-home/outline-tab/messages';
import { Link } from "react-router-dom";
import { getDashboardPlatformURL } from '../utils/utility';
import ProductTours from '../product-tours/ProductTours';
import CourseBreadcrumbs from '../courseware/course/CourseBreadcrumbs';
/** [MM-P2P] Experiment */
import { initCoursewareMMP2P } from '../experiments/mm-p2p';

function LoadedTabPage({
  intl,
  activeTabSlug,
  children = null,
  courseId,
  sectionId,
  sequenceId,
  metadataModel = 'courseHomeMeta',
  unitId = null,
}) {
  const {
    celebrations,
    canViewLegacyCourseware,
    org,
    isStaff,
    originalUserIsStaff,
    tabs,
    title,
    verifiedMode,
  } = useModel('courseHomeMeta', courseId);

  // Logistration and enrollment alerts are only really used for the outline tab, but loaded here to put them above
  // breadcrumbs when they are visible.
  const logistrationAlert = useLogistrationAlert(courseId);
  const enrollmentAlert = useEnrollmentAlert(courseId);

  const activeTab = tabs.filter(tab => tab.slug === activeTabSlug)[0];

  const streakLengthToCelebrate = celebrations && celebrations.streakLengthToCelebrate;
  const streakDiscountCouponEnabled = celebrations && celebrations.streakDiscountEnabled && verifiedMode;
  const [isStreakCelebrationOpen,, closeStreakCelebration] = useToggle(streakLengthToCelebrate);
  const isCourseware = activeTabSlug === 'courseware';

  /** [MM-P2P] Experiment */
  const MMP2P = initCoursewareMMP2P(courseId, sequenceId, unitId);

  return (
    <>
      <ProductTours
        activeTab={activeTabSlug}
        courseId={courseId}
        isStreakCelebrationOpen={isStreakCelebrationOpen}
        org={org}
      />
      <Helmet>
        <title>{`${activeTab ? `${activeTab.title} | ` : ''}${title} | ${getConfig().SITE_NAME}`}</title>
      </Helmet>
      {originalUserIsStaff && (
        <InstructorToolbar
          courseId={courseId}
          unitId={unitId}
          canViewLegacyCourseware={canViewLegacyCourseware}
          tab={activeTabSlug}
        />
      )}
      <StreakModal
        courseId={courseId}
        metadataModel={metadataModel}
        streakLengthToCelebrate={streakLengthToCelebrate}
        isStreakCelebrationOpen={!!isStreakCelebrationOpen}
        closeStreakCelebration={closeStreakCelebration}
        streakDiscountCouponEnabled={streakDiscountCouponEnabled}
        verifiedMode={verifiedMode}
      />
      <main id="main-content" className="d-flex flex-column flex-grow-1">
        {isCourseware ? (
          <div className="position-relative d-flex align-items-start breadcrumbs-wrapper mobile-breadcrumbs d-lg-none">
            <CourseBreadcrumbs
              courseId={courseId}
              sectionId={sectionId}
              sequenceId={sequenceId}
              isStaff={isStaff}
              unitId={unitId}
              //* * [MM-P2P] Experiment */
              mmp2p={MMP2P}
            />
          </div>
        ) : 
            <div className="position-relative breadcrumbs-wrapper px-2 pb-2 mobile-breadcrumbs d-lg-none">
              <nav aria-label="breadcrumb" className="mt-4 d-inline-block">
                <ol className="list-unstyled d-flex flex-wrap flex-lg-nowrap align-items-center m-0">
                  <li>
                    <Link
                      className="flex-shrink-0"
                      to={`#`}
                      onClick={() => window.location.assign(getDashboardPlatformURL())}
                      >
                        {intl.formatMessage(messages.homeBreadCrumb)}
                    </Link>
                  </li>
                  <li><span>/</span></li>
                  <li><a className="flex-shrink-0 color-dark-grey" href="#">{title}</a></li>
                </ol>
              </nav>
            </div>
        }

        <AlertList
          topic="outline"
          className="mx-5 mt-3"
          customAlerts={{
            ...enrollmentAlert,
            ...logistrationAlert,
          }}
        />
        <div className='container-fluid d-flex flex-column flex-grow-1'>
          <div className={classNames('d-flex flex-lg-nowrap flex-wrap flex-grow-1 ', { 'flex-column': isCourseware })}>
            <div className={classNames('overview-sidebar ', {'d-none': isCourseware})}>
              <CourseTabsNavigation courseName={title} tabs={tabs} className="mb-3" activeTabSlug={activeTabSlug} />
            </div>
            <div className='w-100 d-flex flex-column flex-grow-1'>
                {children}
            </div>
          </div>
        </div>
      </main>
    </>
  );
}

LoadedTabPage.propTypes = {
  activeTabSlug: PropTypes.string.isRequired,
  children: PropTypes.node,
  courseId: PropTypes.string.isRequired,
  metadataModel: PropTypes.string,
  unitId: PropTypes.string,
  intl: intlShape.isRequired,
};

export default injectIntl(LoadedTabPage);
